import type {Dimensions, ExifOrientation} from '../types';
import {isExifApp1, readOrientationFromExifApp1} from './exif-app1';
import {walkJpegSegments} from './jpeg-segments';
import type {JpegWalkStatus} from './jpeg-segments';

export type JpegHeaderScan = {
  /** Stored (un-oriented) frame size, or `null` when no frame header was seen. */
  readonly frame: Dimensions | null;
  /** Orientation from the first Exif APP1, or `null` when none was seen. */
  readonly orientation: ExifOrientation | null;
  /** `complete` means every header segment was read; only then is a `null` orientation "absent". */
  readonly status: JpegWalkStatus;
};

const SOF_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

const readFrame = (bytes: Uint8Array, dataOffset: number): Dimensions | null => {
  if (dataOffset + 5 > bytes.length) {
    return null;
  }
  const frame = new DataView(bytes.buffer, bytes.byteOffset + dataOffset, 5);
  return {height: frame.getUint16(1), width: frame.getUint16(3)};
};

/**
 * One pass over the header segments collecting the frame size and the Exif
 * orientation. Camera JPEGs can carry megabytes of ICC/MPF/XMP before the
 * frame header, so callers extend the buffer while `status` is `truncated`.
 */
export const inspectJpegHeader = (bytes: Uint8Array): JpegHeaderScan => {
  let frame: Dimensions | null = null;
  let orientation: ExifOrientation | null = null;
  let orientationSeen = false;
  const segments = walkJpegSegments(bytes);
  let step = segments.next();
  while (!step.done) {
    const segment = step.value;
    if (frame === null && SOF_MARKERS.has(segment.marker)) {
      frame = readFrame(bytes, segment.dataOffset);
    } else if (!orientationSeen && isExifApp1(bytes, segment)) {
      orientationSeen = true;
      orientation = readOrientationFromExifApp1(bytes, segment);
    }
    step = segments.next();
  }
  return {frame, orientation, status: step.value};
};
